/**
 * `Silkscreen` — a screen-printed panel label. `docs/design-direction.md` §6.12.
 *
 * Mono 700 caps at `--fs-11` with +0.16em tracking in `--text-3`, sitting on a
 * control group's bezel line: `CH.LEVEL`, `SIDE A`, `CUE`, `OUT 01`, `SHUFFLE`,
 * `REPEAT`, `SPEED`. It is the cheapest and strongest device in the whole system for
 * making the UI read as an object rather than as a page, and it costs one span.
 *
 * The ink is the global `.silkscreen` recipe in `styles/texture.css`; this component
 * adds the type-family classes — so §4.4's font-failure degradation can find the text
 * — and one guard.
 *
 * **The guard: tracked uppercase Cyrillic is banned** (§4.3, §12.16). The 11px mono
 * tier is licensed for Latin and numerals; +0.16em on Russian caps reads as shouting.
 * CSS cannot see the script of a string, so this component detects it with
 * `hasCyrillic` from `@bl/shared` and degrades the label rather than shouting it: the
 * tracking drops and the forced uppercase comes off, so the copy sets as authored.
 * Development also says so out loud, because a silkscreen label should be Latin
 * control vocabulary in the first place — the scene already uses it. It reports
 * rather than throws: a label in the wrong script is a copy decision to revisit, not
 * a reason to take a screen down, and the degraded form is still shipworthy.
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface SilkscreenProps {
  /** The printed label. Latin control vocabulary and numerals (§6.12, §11). */
  readonly children: string;
  /**
   * `span` (default) for a label beside its control, `legend` when the group is a
   * real `<fieldset>` — a silkscreen label is often exactly a group name.
   */
  readonly as?: 'span' | 'legend' | 'div';
  readonly className?: string | undefined;
}

export function Silkscreen({ children, as = 'span', className }: SilkscreenProps): JSX.Element {
  const cyrillic = hasCyrillic(children);

  if (import.meta.env.DEV && cyrillic) {
    console.error(
      `Silkscreen: ${JSON.stringify(children)} is Cyrillic, so it prints untracked ` +
        'and unforced. The 11px mono tier is licensed for Latin and numerals only — ' +
        'tracked uppercase Cyrillic reads as shouting (design-direction.md §4.3, ' +
        '§12.16). Prefer Latin control vocabulary, or set the copy in the UI tier.',
    );
  }

  const Tag = as;

  return (
    <Tag
      className={cx('silkscreen', 't-mono', 't-11', cyrillic && styles.cyr, className)}
      data-silkscreen="true"
    >
      {children}
    </Tag>
  );
}
